import { t as msg, formatNumber } from "../lib/i18n";
import { useEffect, useState } from "react";
import type { User, Person } from "../../../../packages/shared/src/types";
import { api } from "../lib/api";
type Binding = { cardCount: number; peers: Person[]; accountId: string };
export function AccountConnections({
  user,
  reloadUser,
}: {
  user: User;
  reloadUser: () => Promise<void>;
}) {
  const [binding, setBinding] = useState<Binding | null>(null),
    [peers, setPeers] = useState<Person[]>([]),
    [busy, setBusy] = useState(""),
    [error, setError] = useState("");
  useEffect(() => {
    let live = true;
    api<Binding>("/api/guest/binding")
      .then((r) => {
        if (live) setBinding(r);
      })
      .catch((e) => {
        if (live) setError(e.message);
      });
    if (!user.relationshipsMigrated)
      api<{ peers: Person[] }>("/api/relationships/preview")
        .then((r) => {
          if (live) setPeers(r.peers);
        })
        .catch((e) => {
          if (live) setError(e.message);
        });
    return () => {
      live = false;
    };
  }, [user.id, user.relationshipsMigrated]);
  async function act(kind: "bind" | "enable") {
    if (busy) return;
    setBusy(kind);
    setError("");
    try {
      if (kind === "bind") {
        await api("/api/guest/binding", "POST", {
          confirmed: true,
          accountId: user.id,
        });
        setBinding(null);
      } else {
        let complete = false;
        while (!complete)
          complete = (
            await api<{ complete: boolean }>(
              "/api/relationships/enable",
              "POST",
              { confirmed: true },
            )
          ).complete;
      }
      await reloadUser().catch(() => {});
      location.reload();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy("");
    }
  }
  if (!binding?.cardCount && user.relationshipsMigrated && !error) return null;
  return (
    <aside
      className="account-connections"
      aria-label={msg("Cards and connections")}
    >
      {!!binding?.cardCount && (
        <div className="notice">
          <h2>{msg("Keep these cards with your account.")}</h2>
          <p>
            {msg("{count} received cards are kept in this browser. Bind them to this account to open them on any device. Their senders will see your profile name; positive account exchanges create mutual friendships.", {count:formatNumber(binding.cardCount)})}</p>
          <p className="muted">
            {msg("From {names}. These cards cannot be transferred to another account afterward.",{names:binding.peers.map(p=>p.displayName).join(", ")})}</p>
          <button
            className="button secondary"
            disabled={!!busy}
            onClick={() => void act("bind")}
          >
            {busy === "bind"
              ? msg("Keeping your cards…")
              : msg("Bind cards to my account")}
          </button>
        </div>
      )}
      {!user.relationshipsMigrated && (
        <div className="notice">
          <h2>{msg("Your promises connect you.")}</h2>
          <p>
            {msg("When you accept or fulfil a promise together and both people have accounts, you become mutual friends and can send directly to each other’s received cards. Your profile name is visible to these friends; your email stays private. You can remove or block a friend.")}</p>
          <p className="muted">
            {peers.length ? msg("Existing positive exchanges with {names} will also connect you.",{names:peers.map(p=>p.displayName).join(", ")}) : msg("Existing positive exchanges will also connect you when the other person enables this feature.")}
          </p>
          <button
            className="button secondary"
            disabled={!!busy}
            onClick={() => void act("enable")}
          >
            {busy === "enable" ? msg("Connecting your cards…") : msg("Enable friends")}
          </button>
        </div>
      )}
      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}
    </aside>
  );
}
